import {forwardRef, ReactNode, useEffect, useMemo, useState} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import dynamic from 'next/dynamic'

import InvoiceTemplate1 from 'components/blocks/documents/DocumentInvoice/templates/InvoiceTemplate1'
import Button from 'components/elements/Button'
import SpinnerDots from 'components/elements/SpinnerDots'
import defaultValues from 'src/components/blocks/documents/DocumentInvoice/Invoice.defaultValues'
import {proxyDefaultWrapper} from 'src/utils'
import {useInvoiceStore} from 'src/utils/contexts/RootStore.context'

import s from './InvoiceChooseTemplate.module.scss'
import {TemplateThemes} from './TemplateThemes'

const cx = cn.bind(s)

const Loading = () => (
	<div className={cx('loading')}>
		<SpinnerDots size="m" position="center" />
	</div>
)

const InvoiceTemplate2 = dynamic(
	() => import('components/blocks/documents/DocumentInvoice/templates/InvoiceTemplate2'),
	{
		loading: Loading,
	},
)
const InvoiceTemplate3 = dynamic(
	() => import('components/blocks/documents/DocumentInvoice/templates/InvoiceTemplate3'),
	{
		loading: Loading,
	},
)
const InvoiceTemplate4 = dynamic(
	() => import('components/blocks/documents/DocumentInvoice/templates/InvoiceTemplate4'),
	{
		loading: Loading,
	},
)
const InvoiceTemplate5 = dynamic(
	() => import('components/blocks/documents/DocumentInvoice/templates/InvoiceTemplate5'),
	{
		loading: Loading,
	},
)
const InvoiceTemplate6 = dynamic(
	() => import('components/blocks/documents/DocumentInvoice/templates/InvoiceTemplate6'),
	{
		loading: Loading,
	},
)

interface IPayStubChooseTemplateProps {
	className?: string
	children?: ReactNode | string
	templates: IPayStubTemplates[]
	activeTemplate: IPayStubTemplates
	isPreview?: boolean
	handleTemplateTheme: (id: number, theme: IPayStubTemplates['theme']) => void
	handleActiveTemplate: (id: number) => void
}

const InvoiceChooseTemplate = forwardRef<HTMLDivElement, IPayStubChooseTemplateProps>(
	(
		{
			className,
			children,
			templates,
			activeTemplate,
			handleTemplateTheme,
			handleActiveTemplate,
			isPreview,
			...props
		},
		ref,
	) => {
		const [isMounted, setMounted] = useState(false)
		const store = useInvoiceStore()
		const storeWithDefaults = proxyDefaultWrapper(store, defaultValues)
		useEffect(() => setMounted(true), [])

		const ChosenTemplate = useMemo(() => {
			switch (activeTemplate?.id) {
				case 2:
					return InvoiceTemplate2
				case 3:
					return InvoiceTemplate3
				case 4:
					return InvoiceTemplate4
				case 5:
					return InvoiceTemplate5
				case 6:
					return InvoiceTemplate6

				default:
					return InvoiceTemplate1
			}
		}, [activeTemplate?.id])

		return (
			<div className={cx('template', className)} ref={ref} {...props}>
				{children}
				<ul className={cx('template--list', 'list')}>
					{templates.map(({id, isActive}) => (
						<li key={id} className={cx('list__item')} data-testid="list-item">
							<Button
								className={cx('list__btn', isActive ? 'active' : '')}
								onClick={() => handleActiveTemplate(id)}
								type="button"
								size="small"
								variant="secondary">
								{id}
							</Button>
						</li>
					))}
				</ul>

				{activeTemplate?.theme && (
					<TemplateThemes
						activeTemplate={activeTemplate}
						handleTemplateTheme={handleTemplateTheme}
					/>
				)}

				<div className={cx('template', 'template--preview', 'scroll--preview')}>
					{isMounted && (
						<ChosenTemplate
							className={cx('preview')}
							data={storeWithDefaults}
							hasFrame={true}
							theme={activeTemplate?.theme}
							isPreview={isPreview}
						/>
					)}
				</div>
			</div>
		)
	},
)

InvoiceChooseTemplate.displayName = 'PayStubChooseTemplate'

export default observer(InvoiceChooseTemplate)
